import { useEffect, useRef } from "react";
import Sortable, { SortableEvent } from "sortablejs";
import { Tag, Tooltip } from "antd";
import { Edit2, Trash2, Image as ImageIcon } from "react-feather";
import ToggleSwitch from "../../../CommonComponents/ToggleSwitch/ToggleSwitch";
import ClipPath from "../../../../assets/icons/ic_clip-path.svg";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useSportsbookFm } from "../SportsbookFmContext";
import type { SportsbookListTranslations } from "../Translations/listTranslationData";

export type ShortcutTargetType = "LEAGUE" | "SPORT" | (string & {});

export interface ShortcutItem {
  entity_id: string;
  display_name: string;
  target_name: string;
  target_type: ShortcutTargetType;
  is_active: boolean;
  image_link?: string | null;
  country_tag?: string | null;
  country_name?: string | null;
  translations?: SportsbookListTranslations | null;
}

export interface ShortcutsProps {
  items: ShortcutItem[];
  onRename: (item: ShortcutItem) => void;
  onEditLogo?: (item: ShortcutItem) => void;
  onToggleActive: (item: ShortcutItem) => void;
  onDelete: (id: string) => void;
  onReorder?: (orderedIds: string[]) => void;
  busyIds?: Record<string, boolean>;
}

// Pure layout component for Shortcuts rows. No data fetching.
const Shortcuts: React.FC<ShortcutsProps> = ({
  items,
  onRename,
  onEditLogo,
  onToggleActive,
  onDelete,
  onReorder,
  busyIds = {},
}) => {
  const { canEdit } = useSportsbookFm();
  const listRef = useRef<HTMLDivElement | null>(null);
  const sortableRef = useRef<Sortable | null>(null);

  useEffect(() => {
    if (!listRef.current) return;
    
    // Destroy existing sortable instance
    if (sortableRef.current) {
      try {
        sortableRef.current.destroy();
      } catch {
        /* ignore destroy race */
      }
      sortableRef.current = null;
    }
    
    // Only create sortable if canEdit is true
    if (!canEdit) return;
    
    const element = listRef.current;
    sortableRef.current = Sortable.create(element, {
      animation: 150,
      handle: ".drag-handle",
      draggable: ".shortcut-row",
      onEnd: (evt: SortableEvent) => {
        if (evt.oldIndex === evt.newIndex) return;
        const nodes = Array.from(
          element.querySelectorAll<HTMLElement>(".shortcut-row")
        );
        const orderedIds = nodes
          .map((node) => node.getAttribute("data-id"))
          .filter((id): id is string => Boolean(id));
        onReorder?.(orderedIds);
      },
    });
    return () => {
      try {
        sortableRef.current?.destroy();
      } catch {
        /* ignore destroy race */
      }
      sortableRef.current = null;
    };
  }, [items, onReorder, canEdit]);

  return (
    <div className="shortcuts-table">
      <div className="shortcuts-header">
        <div className="h-order"># {AppStrings.ORDER}</div>
        <div className="h-icon">{AppStrings.ICON}</div>
        <div className="h-name">{AppStrings.DISPLAYED_NAME}</div>
        <div className="h-item">{AppStrings.ITEM}</div>
        <div className="h-status">{AppStrings.STATUS}</div>
        <div className="h-action">{AppStrings.ACTION}</div>
      </div>
      <div className="shortcuts-body" ref={listRef}>
        {items.map((row, idx) => {
          const entityId = String(row.entity_id);
          const isBusy = !!busyIds[entityId];
          return (
            <div className="shortcut-row" key={entityId} data-id={entityId}>
              <div className="order-cell">
                <img
                  className="drag-handle"
                  src={ClipPath}
                  alt={AppStrings.DRAG}
                  style={{ cursor: canEdit ? 'grab' : 'not-allowed', opacity: canEdit ? 1 : 0.5 }}
                />
                <div className="order-pill">{idx + 1}</div>
              </div>
              <div className="icon-cell">
                <div className="shortcut-icon-wrapper">
                  {row.image_link ? (
                    <img
                      className="shortcut-icon"
                      src={row.image_link}
                      alt={AppStrings.ICON_ALT.replace(
                        "{name}",
                        row.display_name
                      )}
                    />
                  ) : (
                    <div
                      className="shortcut-icon placeholder"
                      title={AppStrings.NO_ICON}
                    >
                      <ImageIcon size={18} />
                    </div>
                  )}
                  {onEditLogo ? (
                    <button
                      className="icon-btn ghost shortcut-logo-edit-btn"
                      title={AppStrings.EDIT_SHORTCUT_ICON}
                      onClick={() => onEditLogo(row)}
                      disabled={isBusy || !canEdit}
                    >
                      <Edit2 size={13} />
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="name-cell">
                <span className="name">{row.display_name}</span>
                <button
                  className="icon-btn ghost"
                  title={AppStrings.RENAME}
                  onClick={() => onRename(row)}
                  disabled={isBusy || !canEdit}
                >
                  <Edit2 size={14} />
                </button>
              </div>
              <div className="item-cell">
                <span className="item-name">{row.target_name}</span>
                <Tag
                  color={
                    row.target_type === "LEAGUE"
                      ? "blue"
                      : row.target_type === "SPORT"
                      ? "green"
                      : "gold"
                  }
                >
                  {row.target_type}
                </Tag>
                {row.target_type === "LEAGUE" &&
                (row.country_tag || row.country_name) ? (
                  <Tag color="purple">{row.country_tag || row.country_name}</Tag>
                ) : null}
              </div>
              <div className="status-cell">
                <ToggleSwitch
                  status={row.is_active}
                  eventCode={entityId}
                  onlyUpdate={true}
                  data={row}
                  updateEventCode={onToggleActive}
                  disabled={isBusy || !canEdit}
                />
              </div>
              <div className="action-cell">
                <Tooltip title={AppStrings.DELETE}>
                  <button
                    className="icon-btn danger"
                    onClick={() => onDelete(entityId)}
                    disabled={isBusy || !canEdit}
                  >
                    <Trash2 size={16} />
                  </button>
                </Tooltip>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Shortcuts;
